import { ICompanyInfo } from '@vacationtracker/shared/types/company'

import { IUserInfo } from '../../types/users'
import { isWizardDismissed } from '../../util/onboarding-wizard-storage'

/** Variant 1: welcome modal + profile menu. Variant 2: dashboard banner. `off`: not in experiment / disabled. */
export type OnboardingCallCtaVariantId = 'contextual_and_menu' | 'dashboard_inline' | 'off'

/**
 * Maps PostHog multivariate payload to app variant ids.
 * `false` = flag off or user not exposed; `undefined` / `null` = PostHog not loaded yet - all fall through to
 * `off` (same idea as `typeof flag === 'undefined'` guards elsewhere). Unknown strings map to `off` too.
 */
export function mapPosthogVariantKeyToOnboardingCallCtaVariant(key: string | boolean | null | undefined): OnboardingCallCtaVariantId {
  if (typeof key === 'string') {
    const trimmedPosthogKey = key.trim()
    if (trimmedPosthogKey === 'dashboard_inline') {
      return 'dashboard_inline'
    }
    if (trimmedPosthogKey === 'contextual_and_menu') {
      return 'contextual_and_menu'
    }
  }
  return 'off'
}

/** Wide-column / compact welcome onboarding modal (LESS modifier). Only variant 1 (`contextual_and_menu`) for admins; v2 keeps default modal layout. */
export function shouldApplyOnboardingCallWelcomeModalExperimentLayout(authUser: IUserInfo | null | undefined, variant: OnboardingCallCtaVariantId): boolean {
  if (variant !== 'contextual_and_menu') {
    return false
  }
  if (!authUser || authUser.role !== 'Admin') {
    return false
  }
  return true
}

/** Whether to show CTA surfaces: company admin, trial context, variant not `off`. */
export function shouldShowOnboardingCallSurfaces(variant: OnboardingCallCtaVariantId, authCompany: ICompanyInfo | null | undefined, authUser?: IUserInfo | null): boolean {
  if (variant === 'off') {
    return false
  }
  if (!authUser || authUser.role !== 'Admin') {
    return false
  }
  if (isWizardDismissed(authUser.id, authCompany?.id)) {
    return false
  }
  return isCompanyInTrialContext(authCompany)
}

/** Trial / new-user surfaces: trialing subscription and/or company trial period flags from API. */
function isCompanyInTrialContext(authCompany: ICompanyInfo | null | undefined): boolean {
  if (!authCompany) return false
  if (authCompany.subscriptionStatus === 'trialing') return true
  if (authCompany.trialPeriod === 'in_trial' || authCompany.trialPeriod === 'trial_will_end') return true
  return false
}
